import React from "react";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import SpeedIcon from "@mui/icons-material/Speed";
import {
  estimateDefaultSamplingPoints,
  formatDuration,
  normalizeChartSamplingConfig,
} from "../../utils/chartSamplingConfig";
import { CHART_SAMPLING_RANGES } from "../../utils/chartSampling";

const PREVIEW_RANGES = [
  { label: "1 week", days: 7 },
  { label: "1 month", days: 30 },
  { label: "3 months", days: 90 },
  { label: "6 months", days: 180 },
];

const NumberField = ({ label, helper, min, max, step = "1", value, onChange }) => (
  <label className="system-config-sampling-field">
    <span>{label}</span>
    <input type="number" min={min} max={max} step={step} value={value}
      onChange={(event) => onChange(event.target.value)} />
    {helper ? <small>{helper}</small> : null}
  </label>
);

const RangePolicyCard = ({ range, config, update }) => {
  const defaultValue = config.defaultPointsPerHourByRange[range.key];
  const maximumValue = config.maxPointsPerHourByRange[range.key];

  return (
    <article className="system-config-sampling-range-card">
      <div className="system-config-sampling-range-head">
        <span>Time range</span>
        <strong>{range.label}</strong>
      </div>
      <div className="system-config-sampling-range-inputs">
        <label>
          <span>Default</span>
          <input type="number" min="1" max={maximumValue} value={defaultValue}
            onChange={(event) => update({
              defaultPointsPerHourByRange: {
                ...config.defaultPointsPerHourByRange,
                [range.key]: event.target.value,
              },
            })} />
          <small>points/hour</small>
        </label>
        <div className="system-config-sampling-range-divider" aria-hidden="true" />
        <label>
          <span>Maximum</span>
          <input type="number" min="1" max="3600" value={maximumValue}
            onChange={(event) => update({
              maxPointsPerHourByRange: {
                ...config.maxPointsPerHourByRange,
                [range.key]: event.target.value,
              },
            })} />
          <small>points/hour</small>
        </label>
      </div>
      <p>Starts at <strong>{defaultValue}</strong>; hard limit <strong>{maximumValue}</strong>.</p>
    </article>
  );
};

const PreviewTable = ({ config, mode = "device" }) => (
  <div className="system-config-sampling-preview-table">
    <div className="system-config-sampling-preview-row head">
      <span>Range</span><span>Window</span><span>Estimated points</span>
    </div>
    {PREVIEW_RANGES.map((range) => {
      const estimate = estimateDefaultSamplingPoints({ rangeDays: range.days, config, mode });
      return (
        <div className="system-config-sampling-preview-row" key={`${mode}-${range.label}`}>
          <span>{range.label}</span>
          <span>{formatDuration(estimate.windowSeconds)}</span>
          <strong>{estimate.points.toLocaleString()}</strong>
        </div>
      );
    })}
  </div>
);

const SectionHeading = ({ kicker, title, description, badge, accent = false }) => (
  <div className="system-config-sampling-section-head">
    <div>
      <span className="system-config-sampling-kicker">{kicker}</span>
      <h4>{title}</h4>
      <p>{description}</p>
    </div>
    <span className={`system-config-sampling-section-badge ${accent ? "accent" : ""}`}>{badge}</span>
  </div>
);

const ChartSamplingConfigPanel = ({ chartSampling, setChartSampling }) => {
  const config = normalizeChartSamplingConfig(chartSampling);
  const update = (patch) => setChartSampling(normalizeChartSamplingConfig({ ...config, ...patch }));

  return (
    <section className="system-config-panel system-config-sampling-panel">
      <div className="system-config-panel-head">
        <div>
          <h3>Chart sampling</h3>
          <p>Control automatic sampling for existing widgets and safe points-per-hour choices for customized widgets.</p>
        </div>
        <QueryStatsIcon />
      </div>

      <div className="system-config-sampling-section">
        <SectionHeading kicker="Automatic mode" title="Adaptive default sampling"
          description="Legacy behavior used when a widget is set to System default."
          badge="Existing widgets" />

        <label className={`system-config-sampling-switch ${config.enabled ? "enabled" : ""}`}>
          <input type="checkbox" checked={config.enabled}
            onChange={(event) => update({ enabled: event.target.checked })} />
          <span>
            <strong>Adaptive long-range default</strong>
            <small>Enabled: longer ranges reuse the base-range window and progressively include more points. Disabled: every range keeps roughly the same point count.</small>
          </span>
        </label>

        <div className="system-config-sampling-grid system-config-sampling-legacy-grid">
          <NumberField label="Base range days" min="1" max="365"
            value={config.adaptiveBaseRangeDays} onChange={(value) => update({ adaptiveBaseRangeDays: value })}
            helper="The range that preserves the original point count. Recommended: 30 days." />
          <NumberField label="Device Details multiplier" min="1" max="240" step="0.1"
            value={config.deviceLegacyTimeDiffMultiplier} onChange={(value) => update({ deviceLegacyTimeDiffMultiplier: value })}
            helper="Lower values show more points. Legacy value: 12." />
          <NumberField label="Dashboard multiplier" min="1" max="240" step="0.1"
            value={config.dashboardLegacyTimeDiffMultiplier} onChange={(value) => update({ dashboardLegacyTimeDiffMultiplier: value })}
            helper="Separate dashboard calculation. Legacy value: 12." />
          <NumberField label="Global query safety limit" min="100" max="100000"
            value={config.maxGeneratedPoints} onChange={(value) => update({ maxGeneratedPoints: value })}
            helper="Maximum generated points per query across both sampling modes." />
        </div>

        <div className="system-config-sampling-preview-grid">
          {[{ mode: "device", title: "Device Details preview", formula: "timeDiffHours × multiplier × 3" },
            { mode: "dashboard", title: "Dashboard preview", formula: "timeDiffHours × multiplier" }].map((item) => (
            <div className="system-config-sampling-preview-card" key={item.mode}>
              <div className="system-config-sampling-preview-title">
                <SpeedIcon />
                <span><strong>{item.title}</strong><small>Uses {item.formula}</small></span>
              </div>
              <PreviewTable config={config} mode={item.mode} />
            </div>
          ))}
        </div>
      </div>

      <div className="system-config-sampling-section system-config-sampling-policy-section">
        <SectionHeading kicker="Points per hour mode" title="Defaults and safety limits"
          description="Set the starting value and hard ceiling for each selected time range."
          badge="Range policy" accent />
        <div className="system-config-sampling-policy-note">
          <span><strong>Default</strong> is used when no custom value exists.</span>
          <span><strong>Maximum</strong> is the highest value any widget may request.</span>
          <span>Reducing a maximum automatically reduces its default when necessary.</span>
        </div>
        <div className="system-config-sampling-range-grid">
          {CHART_SAMPLING_RANGES.map((range) => (
            <RangePolicyCard key={range.key} range={range} config={config} update={update} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default ChartSamplingConfigPanel;
